import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { useYwuApp } from '../../../contexts/ywu-app-context';
import type { AvailableLanguage } from '../../../i18n/i18nextInit';
import { showErrorToast } from '../../../utils/toastUtils';
import { type ClientPackageCreateDto, type ClientPackageDto, createClientPackage } from '../../generated/client';
import { queryKeys } from '../../query-keys';

const initialCreatePackagePayload: Omit<ClientPackageCreateDto, 'clientId' | 'language'> = {
    name: '',
    comment: '',
    appendixEnabled: true,
    documentsEnabled: true,
    chaptersEnabled: true,
    genericDocumentsEnabled: true,
    clientPackageDocuments: [],
};

export const useCreateClientPackage = (): UseMutationResult<ClientPackageDto, AxiosError, number> => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { activeLanguage } = useYwuApp();

    return useMutation({
        mutationFn: (clientId) =>
            createClientPackage({ body: { ...initialCreatePackagePayload, language: activeLanguage as AvailableLanguage, clientId } }).then(
                (result) => result.data!,
            ),
        onSuccess: (_, clientId) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.clientPackagesForClient(clientId) });
        },
        onError: (error) => showErrorToast({ error, userMessage: t('failureActionGeneric') }),
    });
};
